import GlobalContext from './GlobalContext';
import { useLocation } from 'react-router';
import { TRequestVerifyLoginPost, TVerifyAndCreateDependentParams } from '../api/types';
import API from '../api/api';
import { createContext, FC, useContext, useEffect, useState } from 'react';
import { TUser } from '../util/types';

export enum EMigrateToExistingAccountUiState {
    default,
    ready,
    notInitialized,
    success,
}

type TCustodian = {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
};

type TDependent = {
    profileId: string;
    firstName: string;
    lastName: string;
};

type TFamily = {
    custodian: TUser & { email: string };
    dependents: TUser[];
};

export type TMigrateToExistingAccountRouterState = {
    custodian: TCustodian;
    dependent: TDependent;
};

type TMigrateToExistingAccountContext = {
    migrateToExistingAccountUiState: EMigrateToExistingAccountUiState;
    setMigrateToExistingAccountUiState: (uiState: EMigrateToExistingAccountUiState) => void;
    custodian: TCustodian | null;
    dependent: TDependent | null;
    family: TFamily;
    verifyAndCreateDependent: (passcode: string) => Promise<void>;
    resendVerificationCode: () => Promise<void>;
};

const MigrateToExistingAccountContext = createContext<TMigrateToExistingAccountContext | undefined>(undefined);

export const MigrateToExistingAccountProvider: FC = (props) => {
    const { children } = props;
    const { gatewayAPIBaseUrl, subscriberId } = useContext(GlobalContext);
    const { state: routerState = {} as TMigrateToExistingAccountRouterState } = useLocation();
    const [migrateToExistingAccountUiState, setMigrateToExistingAccountUiState] = useState(
        EMigrateToExistingAccountUiState.default,
    );
    const [custodian, setCustodian] = useState<TCustodian | null>(null);
    const [dependent, setDependent] = useState<TDependent | null>(null);
    const [family, setFamily] = useState<TFamily | null>(null);

    const parseRouterState = () => {
        try {
            const { custodian, dependent } = routerState as TMigrateToExistingAccountRouterState;
            if (custodian && dependent) {
                setCustodian(custodian);
                setDependent(dependent);
                setMigrateToExistingAccountUiState(EMigrateToExistingAccountUiState.ready);
            } else {
                throw Error('missing route params');
            }
        } catch (error) {
            console.error('Failed to parse router state', error);
            setMigrateToExistingAccountUiState(EMigrateToExistingAccountUiState.notInitialized);
        }
    };

    const verifyAndCreateDependent = async (verificationCode: string) => {
        const queryParams: TVerifyAndCreateDependentParams = {
            firstName: dependent.firstName,
            lastName: dependent.lastName,
            userId: custodian.id,
            code: verificationCode,
            profileId: dependent.profileId,
            subscriberId,
        };
        try {
            const response = await API.get.verifyAndCreateDependent(queryParams);
            const dependents = response.data.dependents;
            setFamily({ custodian, dependents });
        } catch (error) {
            throw Error('Failed to verify and create dependent', { cause: error.response?.status });
        }
    };

    const resendVerificationCode = async () => {
        const postObject: TRequestVerifyLoginPost = {
            email: custodian.email,
            userId: custodian.id,
            subscriberId,
        };
        try {
            await API.post.requestVerifyLogin(gatewayAPIBaseUrl, postObject);
        } catch (error) {
            throw Error('Failed to resend verification code', { cause: error.response?.status });
        }
    };

    useEffect(() => {
        switch (migrateToExistingAccountUiState) {
            case EMigrateToExistingAccountUiState.default:
                parseRouterState();
                break;
            default:
                break;
        }
    }, [migrateToExistingAccountUiState]);

    return (
        <MigrateToExistingAccountContext.Provider
            value={{
                migrateToExistingAccountUiState,
                setMigrateToExistingAccountUiState,
                custodian,
                dependent,
                verifyAndCreateDependent,
                resendVerificationCode,
                family,
            }}
        >
            {children}
        </MigrateToExistingAccountContext.Provider>
    );
};

export default MigrateToExistingAccountContext;
